import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCode, faFileText, faTag } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { validateHsnForm, inputFilters, validateDuplicateHsnDescription } from '../../utils/validate';

/**
 * HsnForm
 *
 * Modal form component for creating or editing an HSN code entry.
 * Features:
 * - client-side input filtering for the HSN code field
 * - validation via `validateHsnForm` from utils
 * - displays server-side validation feedback inline (mapped to fields)
 *
 * Props:
 * - isOpen: show/hide the modal
 * - onClose: callback when modal is closed
 * - onSubmit: async function to call with the HSN payload
 * - hsnCode: optional existing HSN object when editing
 * - loading: disables inputs and shows loading state
 * - taxesList / taxesLoading: data for the tax selector
 */
const HsnForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  hsnCode = null, 
  loading = false,
  taxesList = [],
  taxesLoading = false
  , existingHsnCodes = []
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    hsn_code: '',
    hsn_description: '',
    tax_id: ''
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!hsnCode;
  const requiredIndicator = t('common.form.requiredIndicator');

  // Initialize form with HSN code data when editing
  // If `hsnCode` is provided we populate the fields for edit mode.
  useEffect(() => {
    if (isEditing && hsnCode) {
      let taxId = (hsnCode.tax_id ?? hsnCode.tax?.id)?.toString() || '';

      // API doesn't return tax_id — match by gst_rate from taxesList
      if (!taxId && hsnCode.gst_rate != null && taxesList.length > 0) {
        const matched = taxesList.find(t =>
          parseFloat(t.gst ?? t.rate) === parseFloat(hsnCode.gst_rate)
        );
        if (matched) taxId = String(matched.id);
      }

      setFormData({
        hsn_code: hsnCode.hsn_code?.toString() || '',
        hsn_description: hsnCode.hsn_description || '',
        tax_id: taxId
      });
    } else {
      setFormData({
        hsn_code: '',
        hsn_description: '',
        tax_id: ''
      });
    }
    setErrors({});
  }, [hsnCode, isEditing, taxesList]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        hsn_code: '',
        hsn_description: '',
        tax_id: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  /**
   * handleInputChange(e)
   *
   * Generic change handler for form inputs. Applies inputFilters for
   * specific fields (e.g. numeric-only for HSN code) and clears any field
   * level error when the user starts editing that field.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;
    
    // Apply input filters based on field type
    if (name === 'hsn_code') {
      filteredValue = inputFilters.hsnCode(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: filteredValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    // Run shared validation logic and update the `errors` state.
    const formErrors = validateHsnForm(formData);

    // Duplicate description validation using shared helper
    const dupError = validateDuplicateHsnDescription(formData.hsn_description, existingHsnCodes, isEditing && hsnCode ? hsnCode.id : null);
    if (dupError) {
      formErrors.hsn_description = dupError;
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission and validate before calling parent
    e.preventDefault();
    if (loading) return;               // guard double-submit (parent-driven loading)

    if (!validateForm()) {
      return;
    }

    const hsnData = {
      hsn_code: formData.hsn_code,
      hsn_description: formData.hsn_description,
      tax_id: parseInt(formData.tax_id)
    };
    try {
      const result = await onSubmit(hsnData);

      // If server-side validation failed, surface the detail as a field error
      // Expecting result to be an object like { success: false, error: "HSN code '8703' already exists" }
      if (result && result.success === false) {
        const detail = result.error || result.detail || result.message;
        if (detail && typeof detail === 'string') {
          // Map backend detail to the hsn_code field so it shows inline and keep modal open
          setErrors(prev => ({ ...prev, hsn_code: detail }));
          return;
        }
      }
      // Otherwise, assume success or handled by parent which will close the modal
    } catch (err) {
      // Unexpected error while submitting - show a friendly message on the HSN code field
      const msg = (err && err.message) ? err.message : t('common.actions.submit');
      setErrors(prev => ({ ...prev, hsn_code: msg }));
      return;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      {/* Hide scrollbars but keep scrolling functional */}
      <style>{`.hsn-form-modal{ -ms-overflow-style: none; scrollbar-width: none; } .hsn-form-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto hsn-form-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <h2 className="text-xl font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {isEditing ? t('hsnCodes.forms.title.edit') : t('hsnCodes.forms.title.add')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2);
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
            }}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* HSN Code */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faCode} className="mr-2" />
                {`${t('hsnCodes.forms.fields.hsnCode.label')} ${requiredIndicator}`}
              </label>
              <input
                type="text"
                name="hsn_code"
                value={formData.hsn_code}
                onChange={handleInputChange}
                placeholder={t('hsnCodes.forms.fields.hsnCode.placeholder')}
                disabled={loading}
                maxLength="8"
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.hsn_code ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.hsn_code && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.hsn_code && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.hsn_code}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faFileText} className="mr-2" />
                {`${t('hsnCodes.forms.fields.description.label')} ${requiredIndicator}`}
              </label>
              <textarea
                name="hsn_description"
                value={formData.hsn_description}
                onChange={handleInputChange}
                placeholder={t('hsnCodes.forms.fields.description.placeholder')}
                disabled={loading}
                rows="3"
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 resize-none"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: errors.hsn_description ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                  ...(errors.hsn_description && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                }}
              />
              {errors.hsn_description && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.hsn_description}</p>}
            </div>

            {/* Tax ID (select from available taxes) */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                <FontAwesomeIcon icon={faTag} className="mr-2" />
                {`${t('hsnCodes.forms.fields.tax.label')} ${requiredIndicator}`}
              </label>
              <div>
                <select
                  name="tax_id"
                  value={formData.tax_id}
                  onChange={handleInputChange}
                  disabled={loading || taxesLoading}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 bg-white"
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: errors.tax_id ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                    ...(errors.tax_id && { backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) })
                  }}
                >
                  {/* Show current HSN GST rate as placeholder if available in edit mode */}
                  {(() => {
                    if (taxesLoading) {
                      return <option value="">Loading taxes...</option>;
                    }
                    
                    // If editing and HSN has GST rate, show it as the default option but hidden in dropdown
                    if (isEditing && hsnCode) {
                         const gstRate = hsnCode.gst_rate ?? hsnCode.gst ?? hsnCode.rate ?? hsnCode.tax?.gst ?? null;
                         const taxName = hsnCode.tax_name || hsnCode.tax?.name || null;
                      
                      if (gstRate !== null && gstRate !== '') {
                        let currentLabel = '';
                        if (taxName) {
                          currentLabel = `${taxName} - ${Number(gstRate).toFixed(2)}%`;
                        } else {
                          currentLabel = `${Number(gstRate).toFixed(2)}% `;
                        }
                        return <option value="" style={{ display: 'none' }}>{currentLabel}</option>;
                      }
                    }
                    
                    // Default placeholder - show normally in add mode
                    return <option value="" style={{ display: isEditing ? 'none' : 'block' }}>{t('hsnCodes.forms.fields.tax.placeholder')}</option>;
                  })()}
                  
                  {/* Regular tax options */}
                  {taxesList.map(t => (
                    <option key={t.id} value={String(t.id)}>
                         {t?.name ? `${t?.name} - ` : ''}{typeof t?.gst !== 'undefined' ? `${t?.gst}%` : `${t?.rate ?? ''}%`}
                    </option>
                  ))}
                  
                  {/* If editing and current tax_id exists but not in taxesList, show it as an option */}
                  {(() => {
                    if (isEditing && formData.tax_id && hsnCode) {
                      const exists = taxesList.some(t => String(t.id) === String(formData.tax_id));
                      if (!exists) {
                            const gstRate = hsnCode.gst_rate ?? hsnCode.gst ?? hsnCode.rate ?? hsnCode.tax?.gst ?? null;
                           const taxName = hsnCode.tax_name || hsnCode.tax?.name || null;
                        
                        let label = '';
                        if (taxName) {
                          label += taxName;
                          if (gstRate !== null && gstRate !== '') {
                            label += ` - ${Number(gstRate).toFixed(2)}%`;
                          }
                        } else if (gstRate !== null && gstRate !== '') {
                          label = `${Number(gstRate).toFixed(2)}%`;
                        } else {
                          label = 'Current Tax';
                        }
                        
                        return <option key={`current-${formData.tax_id}`} value={String(formData.tax_id)}>{label}</option>;
                      }
                    }
                    return null;
                  })()}
                </select>
              </div>
              {errors.tax_id && <p className="text-sm font-medium mt-2" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.tax_id}</p>}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6" >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1);
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {t('hsnCodes.forms.buttons.cancel')}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.background : UI_COLORS.BUTTONS.PRIMARY.background,
                color: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.text : UI_COLORS.BUTTONS.PRIMARY.text
              }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isEditing ? t('hsnCodes.forms.buttons.submit.loading.edit') : t('hsnCodes.forms.buttons.submit.loading.add')}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faSave} />
                  <span>{isEditing ? t('hsnCodes.forms.buttons.submit.edit') : t('hsnCodes.forms.buttons.submit.add')}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HsnForm;